import React, { forwardRef, isValidElement, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';
import { FormikProps } from 'formik/dist/types';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { BpDesignSystem } from '../../types';
import { useDebounce } from '../../utils/useDebounce';
import { Box } from '../Box';
import { DefaultButton, PrimaryButton, ButtonProps } from '../Button/Button';
import { styled } from '../../styles';
import { AppFormChangeEffect, AppFormChangeEffectProps } from './effects/AppFormChangeEffect';
import { AppFormValidationEffect, AppFormValidationEffectProps } from './effects/AppFormValidationEffect';
import { AppFormControl } from './appFormElements/AppFormControl';
import { AppFormHelperText } from './appFormElements/AppFormHelperText';
import { AppFormLabel } from './appFormElements/AppFormLabel';
import { AppFormInput } from './appFormElements/AppFormInput';
import { AppFormSchemaContext } from './appFormSchema';
import { AppFormCheckbox } from './appFormElements/AppFormCheckbox';
import { AppFormSelect } from './appFormElements/AppFormSelect';
import { AppFormSwitch } from './appFormElements/AppFormSwitch';
import { AppFormDateTimePicker } from './appFormElements/AppFormDateTimePicker';
import { AppFormChipsSelect } from './appFormElements/AppFormChipsSelect';
import { AppFormDatePicker } from './appFormElements/AppFormDatePicker';
import { LeafType, Leaves, Replace } from './types';

export type AppFormChildrenProps<DATA extends {}> = Replace<
  FormikProps<DATA>,
  {
    setFieldValue: SetFieldValue<DATA>;
    setFieldTouched: (field: Leaves<DATA>, isTouched?: boolean, shouldValidate?: boolean) => void;
    showErrors: boolean;
  }
>;

type SetFieldValue<DATA extends {}> = <K extends Leaves<DATA> & string>(
  field: K,
  value: LeafType<DATA, K>,
  shouldValidate?: boolean
) => void;

type Milliseconds = number;

export interface AppFormProps<DATA extends {}>
  extends Pick<AppFormChangeEffectProps<DATA>, 'onChange'>,
    Pick<AppFormValidationEffectProps<DATA>, 'onValidation'>,
    Pick<Partial<FormikProps<DATA>>, 'initialValues' | 'validateOnBlur'> {
  onSubmit?(value: DATA): void;
  onCancel?(): void;
  actionButtons?: React.ReactNode | ((props: AppFormChildrenProps<DATA>) => React.ReactNode);
  submitButtonText?: string;
  cancelButtonText?: string;
  enableReinitialize?: boolean;
  validateOnMount?: boolean;
  debounceValidation?: Milliseconds;
  submitCount?: number;
  validationSchema?: Yup.ObjectSchema<DeepPartial<DATA> | undefined>;
  children: React.ReactNode | ((props: AppFormChildrenProps<DATA>) => React.ReactNode);
  slotProps?: {
    submitButton?: ButtonProps;
    cancelButton?: ButtonProps;
  };
}

const DEFAULT_SCHEMA = Yup.object();

function AppFormComponent<DATA extends {}>(
  {
    initialValues,
    onChange,
    onSubmit,
    onCancel,
    onValidation,
    validationSchema = DEFAULT_SCHEMA as Yup.ObjectSchema<DATA | undefined, object>,
    children,
    submitButtonText,
    cancelButtonText,
    actionButtons,
    validateOnMount = true,
    validateOnBlur = true,
    enableReinitialize = true,
    debounceValidation,
    submitCount,
    slotProps,
    dataTest,
    style,
    sx,
    className,
    ...formikProps
  }: BpDesignSystem.Props<AppFormProps<DATA>>,
  ref: React.Ref<FormikProps<DATA> | null> | undefined
) {
  const { intl } = useIntlBds();
  const isValidationDebounced = typeof debounceValidation === 'number';
  const formik = useRef<FormikProps<DATA>>(null);
  useImperativeHandle(ref, () => formik.current);
  const [schema] = useState(validationSchema);
  const debouncedValidate = useDebounce((values?: FormikValues) => {
    formik.current?.validateForm(values);
  }, debounceValidation ?? 250);

  useEffect(() => {
    if (typeof submitCount === 'number' && formik.current) {
      formik.current.setFormikState((prevState) => ({ ...prevState, submitCount }));
    }
  }, [submitCount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (isValidationDebounced) {
      debouncedValidate(formik.current?.values);
    }
  }, [formik.current?.values, debouncedValidate, isValidationDebounced]);

  return (
    <Formik
      innerRef={formik}
      initialValues={initialValues as DATA}
      validationSchema={schema}
      validateOnMount={validateOnMount}
      validateOnChange={!isValidationDebounced}
      validateOnBlur={validateOnBlur}
      enableReinitialize={enableReinitialize}
      onSubmit={(values) => {
        onSubmit?.(values as DATA);
      }}
      {...formikProps}
    >
      {/* @ts-ignore this component does not support "children" prop properly */}
      {({ ...props }: AppFormChildrenProps<DATA>) => (
        <Box dataTest={dataTest} sx={sx} style={style} className={className}>
          <AppFormSchemaContext.Provider value={schema}>
            <Form>
              <div data-test="app-form-elements-container">
                {isFunction(children)
                  ? children({
                      ...props,
                      showErrors: props.submitCount > 0
                    } as unknown as AppFormChildrenProps<DATA>)
                  : children}
              </div>
              {(actionButtons || onCancel || onSubmit) && (
                <StyledActionWrapper data-test="app-form-action-container">
                  {actionButtons && isFunction(actionButtons) ? actionButtons(props) : actionButtons}
                  {onCancel && (
                    <DefaultButton.Text
                      dataTest="app-form-button-cancel"
                      type="button"
                      onClick={onCancel}
                      {...slotProps?.cancelButton}
                    >
                      {slotProps?.cancelButton?.children ?? cancelButtonText ?? intl.AppForm.cancelButtonText}
                    </DefaultButton.Text>
                  )}
                  {onSubmit && (
                    <PrimaryButton.Contained
                      dataTest="app-form-button-submit"
                      type="submit"
                      {...slotProps?.submitButton}
                    >
                      {slotProps?.submitButton?.children ?? submitButtonText ?? intl.AppForm.submitButtonText}
                    </PrimaryButton.Contained>
                  )}
                </StyledActionWrapper>
              )}
            </Form>
            {onChange && <AppFormChangeEffect values={props.values} onChange={onChange} dirty={props.dirty} />}
            {onValidation && <AppFormValidationEffect errors={props.errors} onValidation={onValidation} />}
          </AppFormSchemaContext.Provider>
        </Box>
      )}
    </Formik>
  );
}
export const AppForm = forwardRef(AppFormComponent) as unknown as (<DATA extends {}>(
  props: AppFormProps<DATA> & { ref?: React.ForwardedRef<FormikProps<DATA>> },
  ref?: React.ForwardedRef<FormikProps<DATA>>
) => ReturnType<typeof AppFormComponent>) & {
  Control: typeof AppFormControl;
  HelperText: typeof AppFormHelperText;
  Label: typeof AppFormLabel;
  Input: typeof AppFormInput;
  Checkbox: typeof AppFormCheckbox;
  Switch: typeof AppFormSwitch;
  Select: typeof AppFormSelect;
  DateTimePicker: typeof AppFormDateTimePicker;
  DatePicker: typeof AppFormDatePicker;
  ChipsSelect: typeof AppFormChipsSelect;
};
AppForm.Control = AppFormControl;
AppForm.HelperText = AppFormHelperText;
AppForm.Label = AppFormLabel;
AppForm.Input = AppFormInput;
AppForm.Checkbox = AppFormCheckbox;
AppForm.Switch = AppFormSwitch;
AppForm.Select = AppFormSelect;
AppForm.DateTimePicker = AppFormDateTimePicker;
AppForm.DatePicker = AppFormDatePicker;
AppForm.ChipsSelect = AppFormChipsSelect;

const StyledActionWrapper = styled(Box)`
  text-align: right;
  margin-top: 16px;

  button + button {
    margin-left: 16px;
  }
`;

function isFunction<T extends {}>(
  element: React.ReactNode | ((props: AppFormChildrenProps<T>) => React.ReactNode)
): element is (props: AppFormChildrenProps<T>) => React.ReactNode {
  return !Array.isArray(element) && !isValidElement(element);
}
